import { motion } from "motion/react";
import { Loader2 } from "lucide-react";
import {
	IconChannelsOverview,
	IconCompetitors,
	IconContentPlan,
	IconCulturalAdaptation,
	IconGoogleAds,
	IconInstagramAds,
	IconMarketOverview,
	IconMediaPlan,
	IconOffer,
	IconTargetPersonas,
	type IconFC,
} from "@/lib/studio-tools";
import { cn } from "@/lib/utils";

interface WorkspaceToolCard {
	id: string;
	label: string;
	icon: IconFC;
	toolSlug: string | null;
}

// Not the shared STUDIO_CARDS: here Content Plan has no slug of its own — it
// opens the platform picker first.
const WORKSPACE_TOOL_CARDS: WorkspaceToolCard[] = [
	{
		id: "s1",
		label: "Market Overview",
		icon: IconMarketOverview,
		toolSlug: "market-overview",
	},
	{
		id: "s2",
		label: "Target Personas",
		icon: IconTargetPersonas,
		toolSlug: "target-personas",
	},
	{
		id: "s3",
		label: "Competitors",
		icon: IconCompetitors,
		toolSlug: "competitors",
	},
	{
		id: "s4",
		label: "Offer (Key Message)",
		icon: IconOffer,
		toolSlug: "offer-and-key-messages",
	},
	{
		id: "s5",
		label: "Cultural Adaptation",
		icon: IconCulturalAdaptation,
		toolSlug: "cultural-adaptation",
	},
	{
		id: "s6",
		label: "Channels Overview",
		icon: IconChannelsOverview,
		toolSlug: "channels-overview",
	},
	{
		id: "s7",
		label: "Media Plan",
		icon: IconMediaPlan,
		toolSlug: "media-plan-and-budget",
	},
	{
		id: "s8",
		label: "Content Plan",
		icon: IconContentPlan,
		toolSlug: null,
	},
	{
		id: "s9",
		label: "Instagram Ads",
		icon: IconInstagramAds,
		toolSlug: null,
	},
	{
		id: "s10",
		label: "Google Ads",
		icon: IconGoogleAds,
		toolSlug: null,
	},
];

interface WorkspaceToolCardsProps {
	loadingTool: string | null;
	onRunTool: (toolSlug: string | null) => void;
	onOpenContentPlan: () => void;
}

/** Studio tool cards — fixed, non-scrollable grid. */
export function WorkspaceToolCards({
	loadingTool,
	onRunTool,
	onOpenContentPlan,
}: WorkspaceToolCardsProps) {
	return (
		<div className="px-4 pt-2 pb-1">
			<div className="grid grid-cols-2 gap-1.5 [&_svg]:size-4">
				{WORKSPACE_TOOL_CARDS.map((card, i) => (
					<motion.button
						key={card.id}
						initial={{ opacity: 0, scale: 0.9 }}
						animate={{ opacity: 1, scale: 1 }}
						transition={{ duration: 0.2, delay: 0.2 + i * 0.03 }}
						onClick={() => {
							if (card.label === "Content Plan") {
								onOpenContentPlan();
							} else {
								onRunTool(card.toolSlug);
							}
						}}
						disabled={
							card.label !== "Content Plan" &&
							(!card.toolSlug || loadingTool === card.toolSlug)
						}
						className={cn(
							"bg-bg-sunken border border-border-subtle rounded-xl p-2 space-y-1 text-left enabled:hover:border-brand-700 enabled:hover:bg-brand-50 transition-all group",
							card.label !== "Content Plan" &&
								!card.toolSlug &&
								"bg-action-disabled-bg cursor-not-allowed",
							loadingTool !== null &&
								loadingTool === card.toolSlug &&
								"opacity-70",
						)}
					>
						<span
							aria-hidden="true"
							className={cn(
								"flex size-4 items-center justify-center rounded-lg text-text-secondary",
								card.label !== "Content Plan" &&
									!card.toolSlug &&
									"text-action-disabled-fg",
							)}
						>
							{loadingTool !== null && loadingTool === card.toolSlug ? (
								<Loader2 size={16} className="animate-spin" />
							) : (
								<card.icon />
							)}
						</span>
						<span
							className={cn(
								"text-xs font-semibold text-text-primary leading-tight block",
								card.label !== "Content Plan" &&
									!card.toolSlug &&
									"text-action-disabled-fg",
							)}
						>
							{card.label}
						</span>
					</motion.button>
				))}
			</div>
		</div>
	);
}
